import type { FC, ReactNode } from 'react'
import type { ImageProps } from 'next/image'

import Image from 'next/image'
import { cn } from '@/lib/utils'

interface MediaImageProps extends Omit<ImageProps, 'src' | 'alt' | 'fill' | 'width' | 'height'> {
    src: string | null | undefined
    alt: string | null | undefined
}

const OPTIMIZABLE = /^https:\/\/[a-z0-9-]+\.cdn\.modulify\.website\//i

/**
 * Fills its positioned parent with an image. Local and storage (CDN) images are optimized;
 * any other address is shown as is, so an image pasted from elsewhere never breaks a page.
 */
export const MediaImage: FC<MediaImageProps> = ({ src, alt, className, unoptimized, ...props }): ReactNode => {
    if (!src) {
        return (
            <div aria-hidden className={cn('absolute inset-0 flex items-center justify-center bg-muted', className)}>
                <span className='font-heading text-3xl text-muted-foreground/50'>
                    V
                </span>
            </div>
        )
    }

    const optimizable = src.startsWith('/') || OPTIMIZABLE.test(src)

    return (
        <Image
            fill
            src={src}
            alt={alt ?? ''}
            className={className}
            unoptimized={unoptimized ?? !optimizable}
            {...props}
        />
    )
}
